import React from "react";
import { api } from "../../api/api";
import { authAPI } from "../../api/authAPI";
import LinearProgress from "@mui/material/LinearProgress";
import CryptoJS from "crypto-js";
import { validatePassword } from "../../helpers";

export enum userRoles {
	ADMIN = "ADMIN",
	USER = "USER",
	None = "None",
	SUPERADMIN = "SUPERADMIN",
	ENR_ADMIN = "ENR_ADMIN",
	SALES_TEAM_LEAD = "SALES_TEAM_LEAD",
	SALES_MANAGER = "SALES_MANAGER",
	AGENT = "AGENT",
}

export interface IAuthUserCompanyOutlet {
	company_outlet_uuid: string;
	company_outlet_name: string;
}

export interface ISessionDevice {
	browser?: string;
	os_name?: string;
	login_at?: string;
	ip_address?: string;
	device_type?: string;
	sessionCount?: number;
	last_active_at?: string;
	[key: string]: any;
}

export const extractSessionList = (responseOrData: any): ISessionDevice[] => {
	if (!responseOrData) return [];
	const targets = [
		responseOrData,
		responseOrData?.data?.devices,
		responseOrData?.data?.session_list,
		responseOrData?.data?.active_sessions,
		responseOrData?.data?.login_devices,
		responseOrData?.sessions,
	];

	for (const target of targets) {
		if (Array.isArray(target) && target.length > 0) {
			if (target.some((item) => item && (item.browser || item.os_name || item.session_count !== undefined || item.device_type))) {
				return target;
			}
		}
	}

	const obj = responseOrData?.data || responseOrData;
	if (obj && typeof obj === "object") {
		for (const key of Object.keys(obj)) {
			const val = obj[key];
			if (Array.isArray(val) && val.length > 0) {
				if (val.some((item) => item && (item.browser || item.os_name || item.session_count !== undefined || item.device_type))) {
					return val;
				}
			}
		}
		const values = Object.values(obj);
		if (values.length > 0 && values.some((item: any) => item && (item.browser || item.os_name || item.session_count !== undefined))) {
			return values.filter((item: any) => item && (item.browser || item.os_name || item.session_count !== undefined)) as ISessionDevice[];
		}
	}

	return [];
};

export interface IAuth {
	user: {

		name: string;
		userId: number;
		email: string;
		isLogin: boolean;
		role: keyof typeof userRoles;
		tokens: { access: string } | null;
		userUuid: string;
		sessionCount: number | null;
		role_value: string;
		office: string;
		manager_uuid: string;
		manager_name: string;
		affilated_company_outlet_uuids: IAuthUserCompanyOutlet[];
	};
	twoFactorData: {
		userFactId: string;
	};
	login: (
		emailOrUsername: string,
		password: string,
		onSuccess: (isOTPPreview: boolean) => void,
		onError: (message: string) => void,
		onSessionLimit?: (data: { sessions: ISessionDevice[]; message: string; logoutToken?: string; userUuid?: string }) => void
	) => void;
	resendOTP: () => void;
	verifyOTP: (
		otp: string,
		onSuccess: () => void,
		onError: (message: string) => void
	) => void;
	forgotPassword: (
		email: string,
		otp_for: string,
		onSuccess: () => void,
		onError: (message: string) => void
	) => void;
	setNewPassword: (
		email: string,
		otp: string,
		new_password: string,
		onSuccess: () => void,
		onError: (message: string) => void
	) => void;
	logout: () => void;
	logoutFromAllDevices: (
		onSuccess?: () => void,
		onError?: (message: string) => void,
		overrideLogoutToken?: string
	) => void;
	logoutSpecificSession: (
		sessionCount: number,
		onSuccess?: () => void,
		onError?: (message: string) => void,
		overrideLogoutToken?: string
	) => Promise<void>;
	addUserName: (name: string) => void;
	otherUserLogin: (
		token: string,
		userId: number,
		onComplete: (isSuccess: boolean, error?: string) => void
	) => void;
}

const AuthContext = React.createContext<IAuth>({
	user: {
		name: "",
		email: "",
		userId: 0,
		role: userRoles.None,
		isLogin: false,
		tokens: null,
		userUuid: "",
		sessionCount: null,
		role_value: "",
		office: "",
		manager_name: "",
		manager_uuid: "",
		affilated_company_outlet_uuids: [],
	},
	twoFactorData: {
		userFactId: "",
	},
	login: () => { },
	resendOTP: () => { },
	verifyOTP: () => { },
	forgotPassword: () => { },
	setNewPassword: () => { },
	logout: () => { },
	logoutFromAllDevices: () => { },
	logoutSpecificSession: async () => { },
	addUserName: () => { },
	otherUserLogin: () => { },
});

export const useAuth = () => React.useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = (
	props
) => {
	const [user, setUser] = React.useState<IAuth["user"]>({
		name: "",
		email: "",
		role: userRoles.None,
		isLogin: false,
		userId: 0,
		tokens: null,
		userUuid: "",
		sessionCount: null,
		office: "",
		role_value: "",
		manager_name: "",
		manager_uuid: "",
		affilated_company_outlet_uuids: [],
	});
	const [twoFactorData, setTwoFactorData] = React.useState<
		IAuth["twoFactorData"]
	>({
		userFactId: "",
	});
	const [password, setPassword] = React.useState("");
	const [loading, setLoading] = React.useState(true);

	React.useEffect(() => {
		const auth = localStorage.getItem("auth");
		if (auth) {
			const data = JSON.parse(auth);
			const date1 = new Date(data.date);
			const date2 = new Date();
			const diff = date2.getHours() - date1.getHours();
			if (diff <= 1) {
				setUser({
					...data,
					name: data.name || data.full_name || "",
					role: data.role || userRoles.None,
				});
			}
		}
		setLoading(false);
	}, []);

	const handleLogin = async (
		emailOrUsername: string,
		password: string,
		onSuccess: (isOTPPreview: boolean) => void,
		onError: (message: string) => void,
		onSessionLimit?: (data: {
			sessions: ISessionDevice[];
			message: string;
			logoutToken?: string;
			userUuid?: string;
		}) => void
	) => {
		try {
			const cipherKey = process.env.REACT_APP_CIPHER_KEY || "";
			const key = CryptoJS.enc.Hex.parse(cipherKey);

			const encrypted = CryptoJS.AES.encrypt(
				CryptoJS.enc.Utf8.parse(password),
				key,
				{
					mode: CryptoJS.mode.ECB,
					padding: CryptoJS.pad.Pkcs7,
				}
			);

			const encryptedPassword = encrypted.ciphertext.toString(CryptoJS.enc.Hex);

			const res = await api.post("/authentication/login", {
				emailOrUsername: emailOrUsername,
				password: encryptedPassword,
			});
			const data = res.data;

			if (res.data?.data?.force_logout === true || res.data?.data?.force_logout === "true") {
				const userUuid = res.data.data.user_uuid || res.data.data.userUuid || "";
				const sessions = extractSessionList(res.data);
				const errorMessage = res.data.message || "Session login limit reached. Please select a session to logout.";
				const logoutToken =
					res.headers?.["auth-token"] ||
					res.headers?.["auth-Token"] ||
					res.data.data?.logoutToken ||
					res.data.data?.logout_token ||
					res.data?.logoutToken ||
					res.data?.logout_token ||
					res.data.data?.token ||
					res.data.data?.tokens ||
					res.data.data?.access_token ||
					res.data?.token ||
					res.data?.access_token;

				localStorage.setItem(
					"auth",
					JSON.stringify({

						force_logout: "true",
						sessions: sessions,
						logoutToken: logoutToken,

					})
				);

				if (onSessionLimit) {
					onSessionLimit({
						userUuid,
						sessions,
						message: errorMessage,
						logoutToken: logoutToken,
					});
				}
				onError(errorMessage);
				return;
			}

			const userFromRes = data.data.user;

			const userData = {
				name: userFromRes.full_name || userFromRes.name || "",
				email: userFromRes.email,
				role: userFromRes.role_name,
				isLogin: data.data.isOTPPreview === false ? true : false,
				userId: userFromRes.user_fact_id,
				tokens: data.data.token,
				userUuid: userFromRes.user_uuid,
				sessionCount: data.data.sessionCount,
				role_value: userFromRes.role_value,
				force_logout: "false",
				office: userFromRes.office,
				manager_name: userFromRes.manager_name,
				manager_uuid: userFromRes.manager_uuid,
				affilated_company_outlet_uuids: userFromRes.affilated_company_outlet_uuids,
			};

			console.log(userData, "userData...........");

			setUser(userData);
			setPassword(password);
			onSuccess(!userData.isLogin);

			if (userData.isLogin) {
				localStorage.setItem(
					"auth",
					JSON.stringify({ ...userData, date: new Date() })
				);
			} else {
				setTwoFactorData({ userFactId: data.data.userFactId });
			}
		} catch (error: any) {
			onError(error.response?.data?.message || "An unexpected error occurred");
		}
	};

	const resendOTP = async () => {
		try {
			const cipherKey = process.env.REACT_APP_CIPHER_KEY || "";
			const key = CryptoJS.enc.Hex.parse(cipherKey);

			const encrypted = CryptoJS.AES.encrypt(
				CryptoJS.enc.Utf8.parse(password),
				key,
				{
					mode: CryptoJS.mode.ECB,
					padding: CryptoJS.pad.Pkcs7,
				}
			);

			const encryptedPassword = encrypted.ciphertext.toString(CryptoJS.enc.Hex);
			await api.post("/authentication/login", {
				email: user.email,
				password: encryptedPassword,
			});
		} catch (error: any) {
			console.log(error);
		}
	};

	const handleVerfiyOTP = async (
		otp: string,
		onSuccess: () => void,
		onError: (message: string) => void
	) => {
		try {
			const res = await api.post("/user/validate-otp-get-token", {
				user_fact_id: twoFactorData.userFactId,
				otp: Number(otp),
			});
			const data = res.data;

			const userData = {
				...user,
				isLogin: true,
				tokens: { access: data.data.access_token },
			};
			setUser(userData);
			localStorage.setItem(
				"auth",
				JSON.stringify({ ...userData, date: new Date() })
			);
			onSuccess();
		} catch (error: any) {
			onError(error.response.data.message);
		}
	};

	const handleLogout = async () => {
		const auth = localStorage.getItem("auth");
		if (auth) {
			const data = JSON.parse(auth);
			try {
				await api.put("/authentication/logout", {
					// user_uuid: data.userUuid,
					sessionCount: data.sessionCount,
				});
			} catch (error: any) {
				console.error("Logout API error:", error.response.data.message);
			}
		}
		localStorage.clear();
		window.open("/auth/login", "_self");
	};

	const handleLogoutSpecificSession = async (
		sessionCount: number,
		onSuccess?: () => void,
		onError?: (message: string) => void,
		overrideUserUuid?: string,
		overrideLogoutToken?: string
	) => {
		const auth = localStorage.getItem("auth");
		const data = auth ? JSON.parse(auth) : {};
		const rawToken =
			overrideLogoutToken ||
			data.logoutToken ||
			data.tokens ||
			data.token ||
			user?.tokens;
		const tokenStr =
			typeof rawToken === "object" && rawToken !== null
				? rawToken.access || rawToken.token || ""
				: rawToken || "";

		const headers: Record<string, string> = {};
		if (tokenStr) {
			headers["auth-token"] = tokenStr;
			// headers["auth-Token"] = tokenStr;
		}

		try {
			if (data.force_logout === "true" || data.force_logout === true || overrideLogoutToken || data.logoutToken) {
				const forcePayload = {
					sessionCount: sessionCount,
				};
				await api.put(authAPI.forceLogout, forcePayload, { headers });
			} else {
				const regularPayload = {
					sessionCount: sessionCount,
				};
				await api.put(authAPI.logout, regularPayload, { headers });
			}

			if (data.force_logout === "true") {
				const remainingSessions = (data.sessions || []).filter(
					(s: any) => (s.sessionCount ?? s.sessionCount) !== sessionCount
				);
				localStorage.setItem(
					"auth",
					JSON.stringify({
						...data,
						sessions: remainingSessions,
						force_logout: remainingSessions.length > 0 ? "true" : "false",
					})
				);
			}

			if (onSuccess) {
				onSuccess();
			}
		} catch (error: any) {
			const errorMsg = error.response?.data?.message || "Failed to logout session";
			if (onError) {
				onError(errorMsg);
			}
		}
	};

	const handleLogoutFromAllDevices = async (
		onSuccess?: () => void,
		onError?: (message: string) => void,
		overrideLogoutToken?: string
	) => {
		const auth = localStorage.getItem("auth");
		const data = auth ? JSON.parse(auth) : {};
		const rawToken =
			overrideLogoutToken ||
			data.logoutToken ||
			data.tokens ||
			data.token ||
			user?.tokens;
		const tokenStr =
			typeof rawToken === "object" && rawToken !== null
				? rawToken.access || rawToken.token || ""
				: rawToken || "";

		const headers: Record<string, string> = {};

		if (tokenStr) {
			headers["auth-token"] = tokenStr;
			// headers["auth-Token"] = tokenStr;
		}

		try {
			if (data.force_logout === "true" || data.force_logout === true || overrideLogoutToken || data.logoutToken) {
				const forcePayload = {
					sessionCount: 0,
				};
				await api.put(authAPI.forceLogout, forcePayload, { headers });
			} else {
				const regularPayload = {
					sessionCount: 0,
				};
				await api.put(authAPI.logout, regularPayload, { headers });
			}

			if (onSuccess) {
				localStorage.removeItem("auth");
				onSuccess();
				return;
			}
		} catch (error: any) {
			console.error("Logout API error:", error.response?.data?.message);
			if (onError) {
				onError(error.response?.data?.message || "Failed to logout all devices");
				return;
			}
		}
		localStorage.clear();
		window.open("/auth/login", "_self");
	};

	const handleForgotPassword = async (
		email: string,
		otp_for: string,
		onSuccess: () => void,
		onError: (message: string) => void
	) => {
		try {
			await api.post("/authentication/generate-otp", {
				email: email,
				otp_for: otp_for,
			});
			onSuccess();
		} catch (error: any) {
			onError(error.response.data.message);
		}
	};

	const handleNewPassword = async (
		email: string,
		otp: string,
		new_password: string,
		onSuccess: () => void,
		onError: (message: string) => void
	) => {
		const passwordValidation = validatePassword(new_password);
		if (!passwordValidation.isValid) {
			onError(
				"Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character."
			);
			return;
		}

		try {
			const cipherKey = process.env.REACT_APP_CIPHER_KEY || "";
			const key = CryptoJS.enc.Hex.parse(cipherKey);

			const encrypted = CryptoJS.AES.encrypt(
				CryptoJS.enc.Utf8.parse(new_password),
				key,
				{
					mode: CryptoJS.mode.ECB,
					padding: CryptoJS.pad.Pkcs7,
				}
			);

			const encryptedPassword = encrypted.ciphertext.toString(CryptoJS.enc.Hex);

			await api.post("/authentication/forget-password", {
				email: email,
				otp: otp,
				user_password: encryptedPassword,
			});
			onSuccess();
		} catch (error: any) {
			onError(error.response.data.message);
		}
	};

	const addUserName = (name: string) => {
		setUser({ ...user, name: name });
	};

	const otherUserAccess = async (
		token: string,
		userId: any,
		onComplete: (isSuccess: boolean, error?: string) => void
	) => {
		try {
			setLoading(true);
			const userData = {
				name: "",
				email: "",
				role: userRoles.None,
				isLogin: true,
				userId: userId,
				userUuid: userId,
				tokens: { access: token },
				sessionCount: null,
				role_value: "",
				office: "",
				manager_uuid: "",
				manager_name: "",
				affilated_company_outlet_uuids: [],
			};
			setUser(userData);
			localStorage.setItem(
				"auth",
				JSON.stringify({ ...userData, date: new Date() })
			);
			onComplete(true);
			setLoading(false);
		} catch (error: any) {
			// onError(error.response.data.message);
		}
	};

	if (loading) {
		return <LinearProgress />;
	}

	return (
		<AuthContext.Provider
			value={{
				user: user,
				login: handleLogin,
				twoFactorData,
				verifyOTP: handleVerfiyOTP,
				forgotPassword: handleForgotPassword,
				setNewPassword: handleNewPassword,
				logout: handleLogout,
				logoutFromAllDevices: handleLogoutFromAllDevices,
				logoutSpecificSession: handleLogoutSpecificSession,
				resendOTP: resendOTP,
				addUserName: addUserName,
				otherUserLogin: otherUserAccess,
			}}
		>
			{props.children}
		</AuthContext.Provider>
	);
};
